import { useState } from "react";

export interface ChoiceListProps {
  /** list of items to display, use - to add a divider */
  readonly items: string[];
  /** groups of items with associated data & an optional name */
  readonly itemGroups?: ChoiceDataGroup[];
  /** string of selected item */
  readonly selectedItem?: string;
  /** if !rows, show a dropdown, otherwise show a list with given number of rows */
  readonly rows?: number;

  readonly onSelect: (name: string, data?: string) => void;
}

export interface ChoiceDataGroup {
  readonly name?: string; /** group name to show in the UI */
  readonly data: string;  /** data passed to onSelect for items in the group */
  readonly items: string[];
}

/**
 * widget that shows a list in a dropdown (or listbox) & allows you to pick one,
 * it's designed to send onSelect() even if the item's already selected,
 * set an item to '-' to add a separator
 */
export function ChoiceList(props: ChoiceListProps) {
  const [eatNext, setEatNext] = useState(false);

  // onChange is called when the selected item changes, e.g., thru a click or arrow key
  // onClick is called when the item is clicked on, even if it's the current selection
  // sometimes both are called, sometimes only one,
  //  so this timeout lets us respond exactly once in each case
  function ignoreNext() {
    setEatNext(true);
    setTimeout(() => setEatNext(false), 0);
  }

  function onClick(target: EventTarget) {
    if (eatNext) return;
    const e = target as HTMLSelectElement;
    // used to trigger an event instead of onChange so the same item can be selected twice in a row,
    // these checks seem to work for figuring out when item has actually been selected
    if (e.tagName === 'OPTION' && e.value !== '') {
      const tracker = e.dataset['tracker'];
      props.onSelect(e.value, tracker);
      ignoreNext();
    }
  }
  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    if (eatNext) return;
    const tracker = e.target.selectedOptions[0].dataset['tracker'];
    props.onSelect(e.target.value, tracker);
    ignoreNext();
  }

  return (
    <span>
      <select value={props.selectedItem} size={props.rows}
        onClick={e => onClick(e.target)} onChange={onChange} >
        {props.items.map((item, i) => (
          item === '-'
            ? <option style={{ background: 'white' }} disabled={true} key={i} />
            : <option value={item} key={item} data-tracker={i}>
              {item}
            </option>
        ))}
        {props.itemGroups === undefined ? undefined : <GroupItems itemGroups={props.itemGroups} />}
      </select>
    </span>
  );
}

function GroupItems(props: { itemGroups: ChoiceDataGroup[] }) {
  return (<>
    {props.itemGroups.map((item, i) => (
      item.name === undefined
        ? item.items.map((opt, j) => (<option key={`${i}-${j}`} value={opt} data-tracker={item.data}>
          {opt}</option>))
        : (<optgroup label={item.name} key={i}>
          {item.items.map((opt, j) => (<option key={`${i}-${j}`} value={opt} data-tracker={item.data}>
            {opt}</option>))}
        </optgroup>)
    ))}
  </>);
}
